import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaPhoneAlt, FaClock, FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Import logo using the correct path
// import logo from '../../assets/images/home/logo/logo-wide.png';
// Logo will be referenced directly from public folder

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Menu item component for cleaner code
  const MenuItem = ({ to, external, className, children, onClick }) => {
    // For external links
    if (external) {
      return (
        <li className="relative group">
          <a 
            href={to}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            onClick={onClick}
          >
            {children}
          </a>
        </li>
      );
    }
    
    // For internal links
    return (
      <li className="relative group">
        <NavLink 
          to={to}
          className={({ isActive }) => 
            isActive 
              ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full font-medium transition-all duration-300"
              : `${className} relative overflow-hidden`
          }
          onClick={onClick}
        >
          {children}
        </NavLink>
      </li>
    );
  };

  return (
    <header className="w-full">
      <motion.div
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg shadow-lg' 
            : 'bg-transparent'
        }`}
        style={{ top: 0, left: 0, right: 0 }}
        initial={false}
        animate={{
          backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.8)' : 'transparent',
          boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'
        }}
      >
        {/* Top Bar */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="container mx-auto px-4 py-2 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <a href="tel:0712407487" className="flex items-center text-white hover:text-yellow-200 font-semibold text-sm transition-colors">
                <FaPhoneAlt className="mr-1 text-yellow-300" /> 071 240 7487
              </a>
              <span className="hidden sm:inline-block border-l border-white/30 h-4 mx-2"></span>
              <p className="font-bold text-sm tracking-wider">INSTITUTE OF STEM EDUCATION</p>
            </div>
            <div className="flex items-center space-x-3">
              <a 
                href="https://www.facebook.com/STEMLabSL/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300"
              >
                <FaFacebook size={16} />
              </a>
              <a 
                href="https://www.instagram.com/_stemlabs.edu.lk_/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:scale-110 transition-transform duration-300"
              >
                <FaInstagram size={16} />
              </a>
            </div>
          </div>
        </div>
        {/* Main Header */}
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="relative z-10">
              <img 
                src="/images/home/logo/logo-wide.png" 
                alt="STEM Labs" 
                className="h-12 transition-transform duration-300 hover:scale-105" 
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:block">
              <ul className="flex items-center space-x-1">
                <MenuItem to="/" className="nav-link text-gray-700 hover:text-indigo-600">
                  Home
                </MenuItem>
                <MenuItem to="/about" className="nav-link text-gray-700 hover:text-indigo-600">
                  About
                </MenuItem>
                <MenuItem to="/courses" className="nav-link text-gray-700 hover:text-indigo-600">
                  Courses
                </MenuItem>
                <MenuItem to="/workshops" className="nav-link text-gray-700 hover:text-indigo-600">
                  Workshops
                </MenuItem>
                <MenuItem to="/events" className="nav-link text-gray-700 hover:text-indigo-600">
                  Events
                </MenuItem>
                <MenuItem to="/gallery" className="nav-link text-gray-700 hover:text-indigo-600">
                  Gallery
                </MenuItem>
                <MenuItem to="/news" className="nav-link text-gray-700 hover:text-indigo-600">
                  News
                </MenuItem>
                <MenuItem to="/student-innovation" className="nav-link text-gray-700 hover:text-indigo-600">
                  Student Innovation
                </MenuItem>
                <MenuItem to="/contact" className="nav-link text-gray-700 hover:text-indigo-600">
                  Contact
                </MenuItem>
                <MenuItem 
                  to="/RC2021" 
                  className="ml-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow-lg hover:from-purple-600 hover:to-blue-500 transition-all duration-300"
                >
                  RC Winners
                </MenuItem>
              </ul>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden text-gray-700 p-2 focus:outline-none"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
              </motion.div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100"
            >
              <div className="container mx-auto px-4 py-4">
                <ul className="space-y-2">
                  <li>
                    <NavLink 
                      to="/" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Home
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/about" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      About
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/courses" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Courses
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/workshops" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Workshops
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/events" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Events
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/gallery" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Gallery
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/news" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      News
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/student-innovation" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Student Innovation
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/contact" 
                      className={({ isActive }) => 
                        isActive 
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg"
                          : "block py-2 px-4 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-300"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Contact
                    </NavLink>
                  </li>
                  <li>
                    <NavLink 
                      to="/RC2021" 
                      className={({ isActive }) =>
                        isActive
                          ? "block py-2 px-4 text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-full font-semibold shadow-lg"
                          : "block py-2 px-4 text-white bg-gradient-to-r from-purple-600 to-blue-500 rounded-full font-semibold shadow-lg hover:from-blue-500 hover:to-purple-600 transition-all duration-300 mt-2"
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      RC Winners
                    </NavLink>
                  </li>
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </header>
  );
};

export default Header; 